"use client";

import { filter, find, groupBy, map, values as _values } from "lodash";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  Ref,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { AiRequest, Gender } from "@/.gql/graphql";
import { useAiRequestsContext, useUndressContext } from "@/providers";
import { createCustomContext } from "@/providers/context";

/**
 * Интерфейс пропсов провайдера.
 */
interface IConfigProviderProps {
  ages: AiRequest[];
  bodys: AiRequest[];
  busts: AiRequest[];
  butts: AiRequest[];
  costumes: AiRequest[];
  environments: AiRequest[];
  hairColors: AiRequest[];
  poses: AiRequest[];
  qualities: AiRequest[];
  sets: AiRequest[];
  storylines: AiRequest[];
  clips: AiRequest[];
  skinColors: AiRequest[];
  styles: AiRequest[];
  activeTabIndex: number;
  setActiveTabIndex: Dispatch<SetStateAction<number>>;
  wrapperRef: Ref<HTMLDivElement>;
  sectionsRef: React.RefObject<Map<number, HTMLDivElement | null>>;
  handleScroll: () => void;
  handleTabClick: (i: number) => () => void;
}

// Создаём контекст для управления настройками Undress
const [Context, useUndressSettingsContext] =
  createCustomContext<IConfigProviderProps>({
    name: "UndressSettingsContext",
  });

export { useUndressSettingsContext };

/**
 * Фильтрует массив объектов по полу (gender).
 * @param items - Исходный массив объектов с полем `gender`
 * @param gender - Текущий выбранный пол
 * @param filterByImage - Фильтровать ли по наличию `image`
 * @returns Отфильтрованный массив, содержащий только объекты с совпадающим `gender` или `Unisex`
 */
const filterByGender = <T extends { gender: Gender; image?: string | null }>(
  items: T[],
  gender: Gender,
  filterByImage?: boolean,
): T[] => {
  const genderFiltered = filter(
    items,
    (item) => item.gender === gender || item.gender === Gender.Unisex,
  );

  if (filterByImage) {
    return filter(genderFiltered, (item) => Boolean(item.image));
  }

  return genderFiltered;
};

/**
 * - Группируем по "collection.en"
 * - Берем только главный элемент (`isGeneralSet`) из каждой группы, если такого нет - берем первый элемент
 * - Преобразуем сгруппированный объект в плоский массив
 * @param items - Исходный массив объектов с полем `gender`
 * @param gender - Текущий выбранный пол
 * @param filterByImage - Фильтровать ли по наличию `image`
 * @returns Отфильтрованный массив, содержащий только объекты с совпадающим `gender` или `Unisex`
 */
const filterByGenderAndGroup = <
  T extends { gender: Gender; image?: string | null; isGeneralSet?: boolean },
>(
  items: T[],
  gender: Gender,
  filterByImage?: boolean,
): T[] => {
  // Группируем и преобразуем данные
  const grouped = _values(
    groupBy(filterByGender(items, gender, filterByImage), "collection.en"),
  ).map((group) => {
    const generalSet = find(group, (item) => item.isGeneralSet);
    return generalSet ? [generalSet] : [group[0]];
  });

  // Явно приводим тип результата к T[]
  return grouped.flat(1) as T[];
};

/**
 * Провайдер для управления настройками Undress.
 */
export const UndressSettingsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    ages,
    bodys,
    busts,
    butts,
    costumes,
    qualities,
    styles,
    poses,
    environments,
    sets,
    storylines,
    clips,
    hairColors,
    skinColors,
  } = useAiRequestsContext();

  const { clickSettings } = useUndressContext();
  const { gender } = clickSettings;

  // >>> Управление табами
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef(new Map<number, HTMLDivElement | null>());

  /**
   * Получает размеры и позицию обёртки контента
   * @param wrapper - DOM-элемент обёртки
   * @returns Объект с top, bottom и clientHeight
   */
  const getWrapperBounds = (wrapper: HTMLDivElement) => ({
    top: wrapper.getBoundingClientRect().top || 0,
    bottom: wrapper.getBoundingClientRect().bottom || 0,
    clientHeight: wrapper.clientHeight,
  });

  /**
   * Вычисляет смещение каждой секции относительно верха обёртки
   * @param sections - Map с DOM-элементами секций
   * @param wrapperTop - Позиция верха обёртки
   * @returns Массив смещений для каждой секции
   */
  const getSectionOffsets = (
    sections: Map<number, HTMLDivElement | null>,
    wrapperTop: number,
  ) =>
    map(
      Array.from(sections.values()),
      (section) => (section?.getBoundingClientRect().top || 0) - wrapperTop,
    );

  /**
   * Получает позицию верха первой секции
   * @param sections - Map с DOM-элементами секций
   * @returns Позиция верха первой секции или 0
   */
  const getFirstSectionTop = (sections: Map<number, HTMLDivElement | null>) =>
    Array.from(sections.values())[0]?.getBoundingClientRect().top || 0;

  /**
   * Получает позицию низа последней секции
   * @param sections - Map с DOM-элементами секций
   * @returns Позиция низа последней секции или 0
   */
  const getLastSectionBottom = (
    sections: Map<number, HTMLDivElement | null>,
  ) => {
    const values = Array.from(sections.values());
    return values[values.length - 1]?.getBoundingClientRect().bottom || 0;
  };

  /**
   * Обработчик прокрутки контента
   * Определяет активную секцию на основе её видимости в viewport
   */
  const handleScroll = useCallback(() => {
    const wrapper = wrapperRef.current;
    const sections = sectionsRef.current;

    // Проверяем наличие необходимых элементов
    if (!wrapper || !sections || sections.size === 0) {
      return;
    }

    // Получаем размеры и позиции элементов
    const {
      top: wrapperTop,
      bottom: wrapperBottom,
      clientHeight,
    } = getWrapperBounds(wrapper);
    const sectionOffsets = getSectionOffsets(sections, wrapperTop);

    // Если первая секция выше верха обёртки - активна первая секция
    if (getFirstSectionTop(sections) >= wrapperTop) {
      setActiveTabIndex(0);
      return;
    }

    // Если последняя секция ниже низа обёртки - активна последняя секция
    if (getLastSectionBottom(sections) <= wrapperBottom) {
      setActiveTabIndex(sections.size - 1);
      return;
    }

    // Вычисляем точку отсчета для определения активной секции
    // 1.3 - коэффициент, определяющий положение точки отсчета
    const targetOffset = clientHeight / 1.3;

    // Находим индекс активной секции
    // Секция считается активной, если её верх находится выше точки отсчета
    const activeIndex = sectionOffsets.reduce(
      (acc, offset, idx) => (offset < targetOffset ? idx : acc),
      0,
    );

    setActiveTabIndex(activeIndex);
  }, [setActiveTabIndex]);

  /**
   * Обрабатывает клик по табу и прокручивает до соответствующего раздела.
   * @param index - Индекс раздела
   */
  const handleTabClick = useCallback(
    (index: number) => () => {
      const section = sectionsRef.current.get(index);
      if (section) {
        wrapperRef.current?.scrollTo({
          top: section.offsetTop - wrapperRef.current.offsetTop - 16,
          behavior: "smooth",
        });
      }
    },
    [],
  );
  // <<< Управление табами

  const settings = useMemo(
    () => ({
      costumes: filterByGender(costumes, gender, true), // Отфильтрованные массивы настроек
      bodys: filterByGender(bodys, gender, true),
      busts: filterByGender(busts, gender, true),
      butts: filterByGender(butts, gender, true),
      ages: filterByGender(ages, gender),
      qualities: filterByGender(qualities, gender),
      styles: filterByGender(styles, gender, true),
      environments: filterByGender(environments, gender, true),
      hairColors: filterByGender(hairColors, gender, true),
      skinColors: filterByGender(skinColors, gender, true),
      poses: filterByGender(poses, gender, true),
      sets: filterByGenderAndGroup(sets, gender, true),
      storylines: filterByGenderAndGroup(storylines, gender, true),
      clips: filterByGender(clips, gender, true),
      activeTabIndex,
      setActiveTabIndex,
      wrapperRef,
      sectionsRef,
      handleScroll,
      handleTabClick,
    }),
    [
      costumes,
      bodys,
      busts,
      butts,
      ages,
      qualities,
      styles,
      environments,
      hairColors,
      skinColors,
      poses,
      sets,
      storylines,
      clips,
      gender,
      activeTabIndex,
      handleScroll,
      handleTabClick,
    ],
  );

  return <Context value={settings}>{children}</Context>;
};
